import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Center,
  Divider,
  HStack,
  Heading,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "native-base";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { Marker, Polyline } from "@react-google-maps/api";
import decodePolyline from "decode-google-map-polyline";
import qs from "qs";
import { FiCalendar, FiClock } from "react-icons/fi";
import HTMLReactParser from "html-react-parser";
import { Calendar } from "@natscale/react-calendar";
import styles from "./styles.module.css";
import API from "../../api";
import { StateContext, StatusContext } from "../../contexts";
import colors from "../../constants/colors";
import routes from "../../routes";
import Image from "../../components/image";
import { capitalize, formatToCurrency } from "../../utils";
import addCircle from "../../assets/icons/add-circle.svg";
import removeCircle from "../../assets/icons/remove-circle.svg";
import Itinerary from "../../components/itinerary";
import Map from "../../components/map";
import mapIcons from "../../constants/mapIcons";
import TourCard from "../../components/tourCard";

function TourPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quotation = useMemo(
    () => searchParams.get("quotation") || null,
    [searchParams],
  );
  const status = useContext(StatusContext);
  const state = useContext(StateContext);
  const [tour, setTour] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [service, setService] = useState({});
  const [prices, setPrices] = useState([]);
  const [map, setMap] = useState();
  const [returnMap, setReturnMap] = useState();
  const [tours, setTours] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [date, setDate] = useState(null);
  const [dates, setDates] = useState({});

  const getTour = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: { data: _tour },
      } = await API().get(`/tours/${params.id}`);
      if (_tour.card4b) {
        setDates(
          Object.fromEntries(
            _tour.card4b.Seasons.flatMap((season) => {
              return season.Calendar.flatMap((calendar) =>
                Array.from({
                  length:
                    moment(calendar.EndDate).diff(
                      moment(calendar.StartDate),
                      "days",
                    ) + 1,
                })
                  .map((_, index) =>
                    moment(calendar.StartDate).add(index, "days"),
                  )
                  .filter(
                    (day) =>
                      day.diff(moment(), "days") >= 0 &&
                      ((day.day() === 1 && calendar.IsMonday) ||
                        (day.day() === 2 && calendar.IsTuesday) ||
                        (day.day() === 3 && calendar.IsWednesday) ||
                        (day.day() === 4 && calendar.IsThursday) ||
                        (day.day() === 5 && calendar.IsFriday) ||
                        (day.day() === 6 && calendar.IsSaturday) ||
                        (day.day() === 0 && calendar.IsSunday)),
                  )
                  .map((day) => [
                    day.format(moment.HTML5_FMT.DATE),
                    season.ProductVariants.map((variant) => ({
                      id: variant.Id,
                      name: variant.NameDictionary[700],
                      cost: variant.Price,
                      quantity: 0,
                    })),
                  ]),
              );
            }),
          ),
        );
      }
      setTour(_tour);
      setService(_tour?.services?.[0] || {});
      setIsLoading(false);
    } catch (error) {
      status.updateErrorMessage(error);
      navigate(routes.home);
    }
  }, [navigate, params.id, status]);

  useEffect(() => {
    getTour();
  }, [getTour]);

  const getTours = useCallback(async () => {
    try {
      const query = qs.stringify({
        filters: {
          isPublic: true,
          status: true,
          id: { $ne: params.id },
          itinerary: {
            type: "origin",
            isReturn: false,
            departureDate: {
              $gte: moment().toISOString(),
            },
          },
        },
      });
      const {
        data: { data: _tours },
      } = await API().get(`/tours?${query}`);
      setTours(_tours);
    } catch (error) {
      status.updateErrorMessage(error);
    }
  }, [params.id, status]);

  useEffect(() => {
    getTours();
  }, [getTours]);

  useEffect(() => {
    if (service?.prices) {
      setPrices(service.prices.map((price) => ({ ...price, quantity: 0 })));
    }
  }, [service]);

  const origin = useMemo(
    () =>
      tour?.itinerary?.find(
        (location) => !location.isReturn && location.type === "origin",
      ),
    [tour],
  );

  const boardingPoints = useMemo(
    () =>
      tour?.itinerary?.filter(
        (location) => !location.isReturn && location.type === "boardingPoint",
      ) || [],
    [tour],
  );

  const intermediateDestinations = useMemo(
    () =>
      tour?.itinerary?.filter(
        (location) =>
          !location.isReturn && location.type === "intermediateDestination",
      ) || [],
    [tour],
  );

  const destination = useMemo(
    () =>
      tour?.itinerary?.find(
        (location) => !location.isReturn && location.type === "destination",
      ),
    [tour],
  );

  const isRoundTrip = useMemo(
    () => tour?.itinerary?.some((location) => location.isReturn),
    [tour],
  );

  const returnOrigin = useMemo(
    () =>
      tour?.itinerary?.find(
        (location) => location.isReturn && location.type === "origin",
      ),
    [tour],
  );

  const returnIntermediateDestinations = useMemo(
    () =>
      tour?.itinerary?.filter(
        (location) =>
          location.isReturn && location.type === "intermediateDestination",
      ) || [],
    [tour],
  );

  const returnBoardingPoints = useMemo(
    () =>
      tour?.itinerary?.filter(
        (location) => location.isReturn && location.type === "boardingPoint",
      ) || [],
    [tour],
  );

  const returnDestination = useMemo(
    () =>
      tour?.itinerary?.find(
        (location) => location.isReturn && location.type === "destination",
      ),
    [tour],
  );

  useEffect(() => {
    if (map) {
      const locations = [
        origin,
        ...boardingPoints,
        ...intermediateDestinations,
        destination,
      ].filter((location) => location.placeId);
      if (locations.length === 1) {
        const [location] = locations;
        map.panTo({
          lat: location.latitude,
          lng: location.longitude,
        });
      } else if (locations.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        locations.forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
        map.fitBounds(bounds);
      }
    }
  }, [boardingPoints, destination, intermediateDestinations, map, origin]);

  useEffect(() => {
    if (returnMap && isRoundTrip) {
      const locations = [
        returnOrigin,
        ...returnIntermediateDestinations,
        ...returnBoardingPoints,
        returnDestination,
      ].filter((location) => location.placeId);
      if (locations.length === 1) {
        const [location] = locations;
        returnMap.panTo({
          lat: location.latitude,
          lng: location.longitude,
        });
      } else if (locations.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        locations.forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
        returnMap.fitBounds(bounds);
      }
    }
  }, [
    isRoundTrip,
    returnBoardingPoints,
    returnDestination,
    returnIntermediateDestinations,
    returnMap,
    returnOrigin,
  ]);

  const onAddPrice = useCallback(
    (price) => {
      let _prices = [...prices];
      _prices = _prices.map((_price) => {
        if (_price.id === price.id) {
          _price.quantity += 1;
        }
        return _price;
      });
      setPrices(_prices);
    },
    [prices],
  );

  const onRemovePrice = useCallback(
    (price) => {
      let _prices = [...prices];
      _prices = _prices.map((_price) => {
        if (_price.id === price.id) {
          _price.quantity -= 1;
        }
        return _price;
      });
      setPrices(_prices);
    },
    [prices],
  );

  const selectedPrices = useMemo(
    () => prices.filter((price) => price.quantity > 0),
    [prices],
  );

  const onContinue = useCallback(async () => {
    try {
      setIsSaving(true);
      let _service = service?.id;
      if (tour.card4b) {
        const { data: serviceId } = await API(state.jwt).post(
          `/services/card4b/${tour.id}`,
          { prices, date },
        );
        _service = serviceId;
      }
      const {
        data: {
          data: { sku },
        },
      } = await API(state.jwt).post("/carts", {
        data: {
          user: state.user.id,
          service: _service,
          items: selectedPrices.flatMap(({ name, cost, quantity }) =>
            Array.from(new Array(quantity)).map(() => ({ name, cost })),
          ),
        },
      });
      navigate({
        pathname: routes.tourSeatSelection.replace(":id", params.id),
        search: qs.stringify({ sku, quotation }, { skipNulls: true }),
      });
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsSaving(false);
  }, [
    state.jwt,
    state.user.id,
    service.id,
    selectedPrices,
    navigate,
    params.id,
    status,
    tour,
    prices,
    date,
    quotation,
  ]);

  useEffect(() => {
    if (date && dates[date]) {
      setPrices(dates[date]);
    }
  }, [date, dates]);

  if (isLoading) {
    return (
      <Center height="70vh">
        <Spinner size="lg" color={colors.primary} />
      </Center>
    );
  }

  return (
    <VStack>
      <Helmet>
        <title>{tour?.name}</title>
        <meta name="description" content={tour?.description} />
      </Helmet>
      {Boolean(tour.photos?.length) && (
        <HStack
          backgroundColor={colors.lighterGray}
          width="full"
          alignItems="stretch"
          height={400}
          marginBottom={10}>
          {tour.photos?.at(0) && (
            <Box flex={7}>
              <Image
                image={tour.photos.at(0)}
                alt={`tour #${tour?.id}-1`}
                objectFit="cover"
              />
            </Box>
          )}
          {(tour.photos?.at(1) || tour.photos?.at(2)) && (
            <VStack flex={3}>
              {tour?.photos?.at(1) && (
                <Box flex={1}>
                  <Image
                    image={tour.photos.at(1)}
                    alt={`tour #${tour?.id}-2`}
                    objectFit="cover"
                  />
                </Box>
              )}
              {tour?.photos?.at(2) && (
                <Box flex={1}>
                  <Image
                    image={tour.photos.at(2)}
                    alt={`tour #${tour?.id}-3`}
                    objectFit="cover"
                  />
                </Box>
              )}
            </VStack>
          )}
        </HStack>
      )}
      {tour?.card4b ? (
        <HStack paddingX={10} alignItems="flex-start" space={10}>
          <VStack flex={70} space={10}>
            <VStack space={5}>
              <Heading color={colors.primary}>{tour?.name}</Heading>
              <VStack>
                <Text color={colors.lightGray}>Operado por</Text>
                <Heading color={colors.lightGray} fontSize="xl">
                  {tour?.agency?.name}
                </Heading>
              </VStack>
              <VStack>{HTMLReactParser(tour?.description)}</VStack>
            </VStack>
            <Divider />
            <VStack space={3}>
              <Heading color={colors.primary} fontSize="xl">
                Paradas
              </Heading>
              <VStack>
                {tour?.itinerary.map((location) => (
                  <Text key={location.id}>{location.name}</Text>
                ))}
              </VStack>
            </VStack>
          </VStack>
          <VStack space={5}>
            <Heading color={colors.primary} fontSize="2xl">
              Boletos y precios
            </Heading>
            <Calendar
              value={moment(date).toDate()}
              onChange={(_date) =>
                setDate(moment(_date).format(moment.HTML5_FMT.DATE))
              }
              size={350}
              isDisabled={(_date) =>
                !Object.keys(dates).includes(
                  moment(_date).format(moment.HTML5_FMT.DATE),
                )
              }
              weekends={[0]}
            />
            <VStack
              paddingX={7}
              paddingY={7}
              borderWidth={1}
              borderColor={colors.primary}
              borderRadius="md"
              space={5}>
              <Text bold fontSize="lg">
                {tour.name}
              </Text>
              <Text bold fontSize="lg">
                ¿Cuántos tiquetes?
              </Text>
              {prices.map((price) => (
                <HStack
                  key={price.id}
                  space={10}
                  justifyContent="space-between">
                  <VStack flex={1}>
                    <Text>{price.name}</Text>
                    <Text>{formatToCurrency(price.cost)}</Text>
                  </VStack>
                  <HStack
                    space={2}
                    borderWidth={1}
                    borderColor={colors.lightGray}
                    borderRadius="7px"
                    width="150px"
                    justifyContent="space-between"
                    padding={2}>
                    <Pressable
                      onPress={() => onRemovePrice(price)}
                      disabled={price.quantity === 0}>
                      <Image
                        image={removeCircle}
                        alt="remove-circle"
                        width={20}
                        height={20}
                      />
                    </Pressable>
                    <Text fontSize="sm">{price.quantity}</Text>
                    <Pressable onPress={() => onAddPrice(price)}>
                      <Image
                        image={addCircle}
                        alt="add-circle"
                        width={20}
                        height={20}
                      />
                    </Pressable>
                  </HStack>
                </HStack>
              ))}
              <HStack space={2} marginTop={5} justifyContent="space-between">
                <VStack>
                  <Text>Total</Text>
                  <Text bold>
                    {formatToCurrency(
                      prices?.reduce(
                        (total, price) => total + price.cost * price.quantity,
                        0,
                      ),
                    )}
                  </Text>
                </VStack>
                <button
                  disabled={!selectedPrices.length || isSaving}
                  onClick={onContinue}>
                  <Box
                    backgroundColor={colors.primary}
                    borderRadius="full"
                    paddingY={2}
                    paddingX={10}>
                    <Text color={colors.white}>Siguiente</Text>
                  </Box>
                </button>
              </HStack>
            </VStack>
          </VStack>
          {}
        </HStack>
      ) : (
        <HStack paddingX={10} alignItems="flex-start" space={10}>
          <VStack flex={70} space={10}>
            <VStack space={5}>
              <Heading color={colors.primary}>{tour?.name}</Heading>
              <VStack>
                <Text color={colors.lightGray}>Operado por</Text>
                <Heading color={colors.lightGray} fontSize="xl">
                  {tour?.agency?.name}
                </Heading>
              </VStack>
              <VStack>{HTMLReactParser(tour?.description)}</VStack>
            </VStack>
            <Divider />
            <VStack space={5}>
              <Heading color={colors.primary} fontSize="xl">
                Selecciona la unidad en la que deseas viajar
              </Heading>
              <HStack
                flexWrap="wrap"
                width="full"
                style={{ columnGap: "2.5%", rowGap: "20px" }}>
                {/* TODO: Create component */}
                {tour?.services?.map((_service) => (
                  <button
                    className={styles.button}
                    key={_service.id}
                    onClick={() => setService(_service)}>
                    <VStack
                      shadow={1}
                      backgroundColor={colors.white}
                      padding={5}
                      borderRadius={10}
                      width="full"
                      alignItems="center"
                      borderWidth={1}
                      borderColor={
                        service?.id === _service.id
                          ? colors.secondary
                          : colors.transparent
                      }>
                      <Image
                        image={_service?.quotation?.vehicleType?.photos?.at(0)}
                        alt={`service #${_service.id}`}
                        width={220}
                        height={150}
                      />
                      <HStack width="full">
                        <VStack flex={1} alignItems="center">
                          <Text bold>Tipo de</Text>
                          <Text bold>unidad</Text>
                        </VStack>
                        <VStack flex={1} alignItems="center">
                          <Text bold>Asientos</Text>
                          <Text bold>disponibles</Text>
                        </VStack>
                        <VStack flex={1} alignItems="center">
                          <Text bold>Asientos</Text>
                          <Text bold>ocupados</Text>
                        </VStack>
                      </HStack>
                      <HStack width="full">
                        <VStack flex={1} alignItems="center">
                          <Text>{_service.quotation.vehicleType.name}</Text>
                        </VStack>
                        <VStack flex={1} alignItems="center">
                          <Text>
                            {_service.quotation.vehicleType.capacity -
                              _service.bookings.length}
                          </Text>
                        </VStack>
                        <VStack flex={1} alignItems="center">
                          <Text>{_service.bookings.length}</Text>
                        </VStack>
                      </HStack>
                    </VStack>
                  </button>
                ))}
              </HStack>
            </VStack>
            <VStack>
              <Heading color={colors.primary} fontSize="xl">
                Consulta tu Itinerario de viaje
              </Heading>
              <HStack space={20} marginTop={10} alignItems="flex-start">
                <VStack maxWidth={300} width="full">
                  <Itinerary itinerary={tour?.itinerary} />
                </VStack>
                <VStack flex={1} space={5}>
                  <VStack>
                    {isRoundTrip && (
                      <Text bold color={colors.primary} fontSize="3xl">
                        Ida
                      </Text>
                    )}
                    <Box height={400} width="full">
                      <Map setRef={setMap}>
                        {Boolean(tour?.polyline) && (
                          <Polyline
                            path={decodePolyline(tour?.polyline)}
                            options={{
                              strokeColor: colors.primary,
                            }}
                          />
                        )}
                        {Boolean(origin.placeId) && (
                          <Marker
                            position={{
                              lat: origin.latitude,
                              lng: origin.longitude,
                            }}
                            icon={mapIcons.origin}
                            title={origin.name}
                          />
                        )}
                        {Boolean(destination.placeId) && (
                          <Marker
                            position={{
                              lat: destination.latitude,
                              lng: destination.longitude,
                            }}
                            icon={mapIcons.destination}
                            title={destination.name}
                          />
                        )}
                        {boardingPoints
                          .filter((boardingPoint) => boardingPoint.placeId)
                          .map((boardingPoint) => (
                            <Marker
                              key={boardingPoint.placeId}
                              position={{
                                lat: boardingPoint.latitude,
                                lng: boardingPoint.longitude,
                              }}
                              icon={mapIcons.boardingPoint}
                              title={boardingPoint.name}
                            />
                          ))}
                        {intermediateDestinations
                          .filter(
                            (intermediateDestination) =>
                              intermediateDestination.placeId,
                          )
                          .map((intermediateDestination) => (
                            <Marker
                              key={intermediateDestination.placeId}
                              position={{
                                lat: intermediateDestination.latitude,
                                lng: intermediateDestination.longitude,
                              }}
                              icon={mapIcons.intermediateDestination}
                              title={intermediateDestination.name}
                            />
                          ))}
                      </Map>
                    </Box>
                  </VStack>
                  {isRoundTrip && (
                    <VStack>
                      <Text bold color={colors.primary} fontSize="3xl">
                        Ida y vuelta (viaje redondo)
                      </Text>
                      <Box height={400} width="full">
                        <Map setRef={setReturnMap}>
                          {Boolean(tour?.returnPolyline) && (
                            <Polyline
                              path={decodePolyline(tour?.returnPolyline)}
                              options={{
                                strokeColor: colors.primary,
                              }}
                            />
                          )}
                          {Boolean(returnOrigin.placeId) && (
                            <Marker
                              position={{
                                lat: returnOrigin.latitude,
                                lng: returnOrigin.longitude,
                              }}
                              icon={mapIcons.origin}
                              title={returnOrigin.name}
                            />
                          )}
                          {returnIntermediateDestinations
                            .filter(
                              (returnIntermediateDestination) =>
                                returnIntermediateDestination.placeId,
                            )
                            .map((returnIntermediateDestination) => (
                              <Marker
                                key={returnIntermediateDestination.placeId}
                                position={{
                                  lat: returnIntermediateDestination.latitude,
                                  lng: returnIntermediateDestination.longitude,
                                }}
                                icon={mapIcons.intermediateDestination}
                                title={returnIntermediateDestination.name}
                              />
                            ))}
                          {returnBoardingPoints
                            .filter(
                              (returnBoardingPoint) =>
                                returnBoardingPoint.placeId,
                            )
                            .map((returnBoardingPoint) => (
                              <Marker
                                key={returnBoardingPoint.placeId}
                                position={{
                                  lat: returnBoardingPoint.latitude,
                                  lng: returnBoardingPoint.longitude,
                                }}
                                icon={mapIcons.boardingPoint}
                                title={returnBoardingPoint.name}
                              />
                            ))}
                          {Boolean(returnDestination.placeId) && (
                            <Marker
                              position={{
                                lat: returnDestination.latitude,
                                lng: returnDestination.longitude,
                              }}
                              icon={mapIcons.destination}
                              title={returnDestination.name}
                            />
                          )}
                        </Map>
                      </Box>
                    </VStack>
                  )}
                </VStack>
              </HStack>
            </VStack>
          </VStack>
          <VStack
            flex={30}
            space={5}
            position="sticky"
            top={5}
            maxHeight="100vh"
            overflowY="auto">
            <Heading color={colors.primary} fontSize="2xl">
              ¡Reserva ya!
            </Heading>
            <VStack>
              <HStack space={2}>
                <FiCalendar color={colors.primary} size={20} />
                <Text>
                  {capitalize(
                    moment(origin?.departureDate).format("ddd DD [de] MMM"),
                  )}
                </Text>
              </HStack>
              <HStack space={2}>
                <FiClock color={colors.primary} size={20} />
                <Text>
                  {capitalize(moment(origin?.departureDate).format("hh:mm a"))}
                </Text>
              </HStack>
              <Text color={colors.text}>
                Tipo de unidad seleccionada:{" "}
                <Text bold>{service?.quotation?.vehicleType?.name}</Text>
              </Text>
            </VStack>
            <VStack
              paddingX={7}
              paddingY={7}
              borderWidth={1}
              borderColor={colors.primary}
              borderRadius="md"
              space={5}>
              <Text bold fontSize="lg">
                {tour.name}
              </Text>
              <Text bold fontSize="lg">
                ¿Cuántos tiquetes?
              </Text>
              {prices.map((price) => (
                <HStack
                  key={price.id}
                  space={10}
                  justifyContent="space-between">
                  <VStack flex={1}>
                    <Text>{price.name}</Text>
                    <Text>{formatToCurrency(price.cost)}</Text>
                  </VStack>
                  <HStack
                    space={2}
                    borderWidth={1}
                    borderColor={colors.lightGray}
                    borderRadius="7px"
                    width="150px"
                    justifyContent="space-between"
                    padding={2}>
                    <Pressable
                      onPress={() => onRemovePrice(price)}
                      disabled={price.quantity === 0}>
                      <Image
                        image={removeCircle}
                        alt="remove-circle"
                        width={20}
                        height={20}
                      />
                    </Pressable>
                    <Text fontSize="sm">{price.quantity}</Text>
                    <Pressable onPress={() => onAddPrice(price)}>
                      <Image
                        image={addCircle}
                        alt="add-circle"
                        width={20}
                        height={20}
                      />
                    </Pressable>
                  </HStack>
                </HStack>
              ))}
              <HStack space={2} marginTop={5} justifyContent="space-between">
                <VStack>
                  <Text>Total</Text>
                  <Text bold>
                    {formatToCurrency(
                      prices?.reduce(
                        (total, price) => total + price.cost * price.quantity,
                        0,
                      ),
                    )}
                  </Text>
                </VStack>
                <button
                  disabled={!selectedPrices.length || isSaving}
                  onClick={onContinue}>
                  <Box
                    backgroundColor={colors.primary}
                    borderRadius="full"
                    paddingY={2}
                    paddingX={10}>
                    <Text color={colors.white}>Siguiente</Text>
                  </Box>
                </button>
              </HStack>
            </VStack>
          </VStack>
        </HStack>
      )}
      {tours.length > 0 && (
        <VStack paddingX={10} space={5} marginTop={10}>
          <Heading color={colors.primary} fontSize="2xl">
            Próximos viajes
          </Heading>
          <HStack overflowX="auto">
            {tours.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </HStack>
        </VStack>
      )}
    </VStack>
  );
}

export default TourPage;
